import cn from 'clsx'
import { replace } from 'connected-react-router'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import styled from 'styled-components'

import { SearchFormRef } from 'components/Search'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  ALL_HOTEL_TYPES,
} from 'constants/offer'
import {
  landmarkSearchSortByTypes,
  searchSortByTypes,
  SORT_OPTION_CUSTOMER_RATING,
} from 'constants/offerListFilters'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import {
  decodeSearchParams,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchTypeFromFilters,
} from 'lib/search/searchUtils'
import {
  deleteSearchParams,
  parseSearchString,
} from 'lib/url/searchUrlUtils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import config from 'constants/config'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import useMetaSearchOfferList from 'hooks/Offers/useMetaSearchOfferList'
import useQueryParams from 'hooks/useQueryParams'
import HotelSearchHeader from './HotelSearchHeader'
import { getSource } from 'selectors/offerSelectors'
import { CompactFooter, OriginalFooter } from 'components/App/Footer'
import {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import Group from '../../utils/Group'
import { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import OfferListOfferTile from 'components/OfferList/OfferListTiles/OfferListOfferTile'
import { generalLog } from 'marketing/api'
import useOffer from 'hooks/Offers/useOffer'
import { isBedbankOffer, isHotel } from 'lib/offer/offerTypes'
import { OfferListEventHandler, OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import * as Analytics from 'analytics/analytics'
import { impressionEventWithContext, productClickEventWithContext, searchEvent } from 'analytics/snowplow/events'
import { fetchMetaSearchOfferRates } from 'actions/OfferActions'
import Error404PageWithOffers from '../Error404PageWithOffers/Error404PageWithOffers'
import HttpStatus from 'components/Common/HttpStatus'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextButton from 'LuxKit/Button/TextButton'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import FilterChip from 'LuxKit/Chips/FilterChip'
import MetaSearchPageHeadData from 'components/Pages/HotelSearchPage/MetaSearchPageHeadData'
import { MetaSearchContext } from 'contexts/MetasearchContext'

const YMAL_LIMIT = 20
const MAIN_OFFER_LIMIT = 1
const METASEARCH_SORT_OPTIONS = [{ label: 'Recommended', value: 'recommendation' }, { label: 'Closest distance', value: 'distance' }, { label: 'Highest discount value', value: 'discount' }, { label: 'Highest rating', value: 'rating' }]

const PageWrapper = styled(Group)`
  height: calc(var(--vh, 1vh) * 100);
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: grid;
    padding-top: ${rem(24)};
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
  margin-top: ${rem(16)};
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    pathName: state.router.location.pathname,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
    region: state.geo.currentRegionCode,
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any> {}

function MetaSearchPage({
  filters,
  searchResultMetadata,
  windowSearch,
  pathName,
  history,
  match,
  region
}: Props) {
  const queryParams = useQueryParams()
  const propertyId = match?.params?.propertyId
  const dispatch = useAppDispatch()
  const isMobile = useScreenSizeOnly('mobile')
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const source = useAppSelector(state => getSource(state))
  const utmContent = useAppSelector(state => state.utm.content)
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const [listSortMode, setListSortMode] = useState<'recommendation' | 'distance' | 'discount' | 'rating'>(queryParams.get('sortBy') as 'recommendation' | 'distance' | 'discount' | 'rating')
  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId || ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const formRef = useRef<SearchFormRef>(null)
  const hideSearchHeader = queryParams.get('hide_search') === 'true'
  const shouldShowPerNightPricing = useOptimizelyExperiment(OptimizelyExperiments.growthMetasearchPerNightPricing, config.BRAND === 'luxuryescapes' && (utmContent === 'gha-lpp' || (utmSource === 'google' && utmMedium === 'cpc')))

  // Track previous destination to detect changes
  const prevDestinationRef = useRef({
    destinationId: params.destinationId,
    propertyId: propertyId,
    landmarkId: params.landmarkId,
  })

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  }, queryParams)

  const listFilters = useMemo((): App.OfferListFilters => {
    return {
      ...filters,
      offerTypes: [...ALL_HOTEL_TYPES],
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      source,
      propertyId: `le:${propertyId}`,
      limit: MAIN_OFFER_LIMIT,
    }
  }, [filters, searchType, source, propertyId])

  const [offer, fetching] = useOffer(propertyId, {
    requireSummaryOnly: true,
  })

  useEffect(() => {
    const childrenQueryParam = queryParams.get('children')
    const adultQueryParam = queryParams.get('adults')
    const propertyNameQueryParam = queryParams.get('propertyName')
    const propertyIdPrefix = isBedbankOffer(offer) ? 'bedbank' : 'le'
    if (listSortMode !== queryParams.get('sortBy')) {
      queryParams.set('sortBy', listSortMode)
    }
    if (!childrenQueryParam?.trim()) {
      queryParams.set('children', 'none')
    }
    if (!adultQueryParam || adultQueryParam === '') {
      queryParams.set('adults', '2')
    }
    queryParams.set('propertyId', `${propertyIdPrefix}:${propertyId}`)
    if (!propertyNameQueryParam) {
      const propertyName = isHotel(offer) ? offer.property?.name : 'Luxury Hotel'
      queryParams.set('propertyName', propertyName)
    }
    history.replace({
      search: queryParams.toString(),
    })
  }, [history, queryParams, propertyId, offer, listSortMode])

  const metaSearchFilters = useMemo((): App.MetaSearchObject | undefined => {
    if (!isBedbankOffer(offer)) return undefined

    return {
      region,
      offerCountry: offer.property?.address.countryCode ?? region,
      latitude: offer.property?.latitude ?? 0,
      longitude: offer.property?.longitude ?? 0,
      disabled: false,
      limit: 20,
      checkIn: filters.checkIn,
      checkOut: filters.checkOut,
      offerId: offer.id,
    }
  }, [offer, region, filters])

  const metaSearchOfferList = useMetaSearchOfferList(metaSearchFilters, {
    disabled: !offer,
    sortMode: listSortMode,
  })

  const sortOptions = useMemo(() => {
    let options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    options = [...options, SORT_OPTION_CUSTOMER_RATING]

    return options
  }, [searchType])

  const Footer = config.footer.type === 'original' ? OriginalFooter : CompactFooter

  const [showFooter, setShowFooter] = useState<boolean>(false)

  const sortInputs = METASEARCH_SORT_OPTIONS.map(option => <FilterChip
    key={option.value}
    selected={listSortMode === option.value}
    onClick={() => setListSortMode(option.value as 'recommendation' | 'distance' | 'discount' | 'rating')}
    name={option.label}
    value={option.value}
    size="medium"
  >
    {option.label}
  </FilterChip>)

  useEffect(() => {
    dispatch(fetchMetaSearchOfferRates({
      propertyId,
      filters: listFilters,
      source,
      regionCode: region,
      offerListKey: getOfferListKey(listFilters),
      alternativeSourceOfferListKey: getOfferListKey(listFilters),
    }))
  }, [dispatch, listFilters, propertyId, region, source])

  useEffect(() => {
    if (!fetching && !offer) {
      const paramObject = Object.fromEntries(queryParams.entries())
      generalLog({
        status: 'error',
        message: 'MetaSearchPage: No results found',
        payload: {
          ...paramObject,
          propertyId,
          fullPath: pathName + windowSearch,
        },
      })
    }
  }, [fetching, offer, pathName, propertyId, queryParams, windowSearch])

  useEffect(() => {
    if (!fetching && offer) {
      setShowFooter(true)
    }
  }, [fetching, offer, isMobile])

  // Detect destination changes when search is submitted
  useEffect(() => {
    const hasDestinationChanged =
      prevDestinationRef.current.destinationId !== params.destinationId ||
      (prevDestinationRef.current.propertyId !== propertyId && prevDestinationRef.current.propertyId !== undefined) ||
      prevDestinationRef.current.landmarkId !== params.landmarkId

    if (hasDestinationChanged) {
      history.replace({
        pathname: `/${region}/search`,
        search: windowSearch,
      })
    }
  }, [
    history,
    region,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    params.landmarkId,
    params.landmarkName,
    propertyId,
    pathName,
    windowSearch,
  ])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'auto', top: 0 })
  }, [dispatch])

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: 'Map view',
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  // Initiate tracking for the meta search page
  const tracking = useOfferListTracking(
    getListName('MetaSearch', 'MetaSearch', 'Meta Search Page'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad) {
      Analytics.trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters,
        searchItem: searchParams.searchItem,
        checkIn: filters.checkIn,
        checkOut: filters.checkOut,
        vertical: 'hotels',
        occupancies: filters.rooms,
      }))
    }
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, price, memberPrice, duration, extendedAvailability, availability } = dispatchAction
      Analytics.trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration },
        ),
      )
      if (extendedAvailability) {
        Analytics.trackClientEvent({
          subject: 'clicked',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, extendedAvailability, roomRateId } = dispatchAction
      Analytics.trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, roomRateId },
        ),
      )
      if (extendedAvailability) {
        Analytics.trackClientEvent({
          subject: 'viewed',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    }
  }, [tracking.listName, tracking.listId, filters, searchParams.searchItem])

  if (!offer && !fetching) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  return <PageWrapper direction="vertical" verticalAlign="space-between" gap={16}>
    <div>
      <MetaSearchContext value={metaSearchOfferList}>
        <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
          <GlobalSearchContextProvider
            state={globalHotelsSearchState}
            dispatch={globalHotelsSearchDispatch}
          >
            { offer && <MetaSearchPageHeadData offer={offer as App.BedbankOffer} searchParams={searchParams} filters={{ ...listFilters }} /> }
            <Group direction="vertical" gap={16}>
              {!hideSearchHeader &&
                <HotelSearchHeader
                  filters={filters}
                  formRef={formRef}
                  offerListFiltersProps={offerListFiltersProps}
                  searchVertical="hotels"
                  baseSearchPath={pathName}
                  baseSearchMapPath="/search/map"
                  onFilterChange={handleFilterChange}
                  onSortChange={handleSortChange}
                  showFilters={false}
                />}
              <CSSBreakpoint max="mobile">
                <LayoutContainer>
                  <HorizontalContainerWithFadedOverflow gap={8}>
                    {sortInputs}
                  </HorizontalContainerWithFadedOverflow>
                </LayoutContainer>
              </CSSBreakpoint>
            </Group>
            <StyledLayoutContainer
              size="xl"
              className={cn({ fullWidth: isMobile })}
            >
              <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                <StyledVerticalSpacer gap={16}>
                  <CSSBreakpoint min="tablet">
                    <Group direction="horizontal" horizontalAlign="space-between">
                      <HorizontalContainerWithFadedOverflow gap={8}>
                        {sortInputs}
                      </HorizontalContainerWithFadedOverflow>
                      <TextButton
                        kind="secondary"
                        variant="dark"
                        to={mapViewURL}
                        startIcon={<LineMapIcon />}
                      >
                        Map view
                      </TextButton>
                    </Group>
                  </CSSBreakpoint>
                  <OfferListOfferTile
                    offerId={propertyId}
                    position={0}
                    eagerLoadImage
                    tileStyle="search"
                    wrapperClass="offer-tile"
                    filters={{ ...listFilters }}
                    isMetaSearch
                  />
                  <MessageBanner kind="promotional-primary" icon={<LineInfoCircleIcon />}>
                    Save up to 58% on these exclusive nearby offers!
                  </MessageBanner>
                  {metaSearchOfferList.map(({ offerId, type, checkIn, checkOut }, index) => {
                    let filtersForTile: App.OfferListFilters = { limit: YMAL_LIMIT, lere: 'alternatives', showDistanceWithMetaOfferId: propertyId }
                    if (type === 'pinned') {
                      filtersForTile = { showDistanceWithMetaOfferId: propertyId, checkIn, checkOut }
                    } else {
                      filtersForTile = { showDistanceWithMetaOfferId: propertyId }
                    }

                    return (
                      <OfferListOfferTile
                        key={offerId}
                        offerId={offerId}
                        position={index}
                        eagerLoadImage
                        tileStyle="search"
                        wrapperClass="offer-tile"
                        filters={filtersForTile}
                        showPerNightPricing={shouldShowPerNightPricing}
                        showLimitedTimeLuxExclusiveTooltip={type !== 'recommended'}
                      />
                    )
                  })}
                </StyledVerticalSpacer>
              </OfferListEventsProvider>
            </StyledLayoutContainer>
          </GlobalSearchContextProvider>
        </AnalyticsPageContext.Provider>
      </MetaSearchContext>
    </div>
    {showFooter && <Footer />}
  </PageWrapper>
}

export default withRouter(connector(MetaSearchPage))
